/**
 * Export & Reporting Action Definitions
 *
 * Actions: export-compounds, export-selection, export-query, get-protein-pdb, get-mol-pic
 *
 * @module gibbs/registry/actions/export
 */

import { WorkflowTypes as T } from '../types';
import { param, pre } from '../schema';

// ─── Format options shared across export actions ────────────────────────────

const EXPORT_FORMATS = ['sdf', 'mol', 'smi', 'inchi', 'inchikey', 'csv', 'xyz', 'mol2', 'pdb', 'smiNoName'];

// ─── export-compounds ───────────────────────────────────────────────────────

export const exportCompounds = {
    id: 'export-compounds',
    name: 'Export Compounds to Format',
    category: 'Export & Reporting',
    description: 'Export one or more compounds to molecular file formats. Supports local (cached) export for common formats and server-side export for others.',
    inputs: [
        param('compounds', T.CompoundList, 'Compounds to export'),
        param('format', 'string', 'Target format', true, { options: EXPORT_FORMATS }),
    ],
    outputs: [
        param('data', 'string', 'Exported molecular data'),
    ],
    parameters: [
        param('format', 'string', 'Export format', true, { options: EXPORT_FORMATS }),
    ],
    dependsOn: [],
    preconditions: [
        pre('compound_loaded', 'Compounds must be loaded'),
    ],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'ExportCompoundsToFormat',
    serverCommand: 'export-selection',
    jobType: null,
};

// ─── export-selection ───────────────────────────────────────────────────────

export const exportSelection = {
    id: 'export-selection',
    name: 'Export Selection to Format',
    category: 'Export & Reporting',
    description: 'Export the currently selected atoms (protein residues, ligand atoms, etc.) to a molecular format.',
    inputs: [
        param('format', 'string', 'Target format', true, { options: EXPORT_FORMATS }),
        param('caseData', T.CaseData, 'Protein case context'),
    ],
    outputs: [
        param('data', 'string', 'Exported atomic data'),
    ],
    parameters: [
        param('format', 'string', 'Export format', true, { options: EXPORT_FORMATS }),
    ],
    dependsOn: ['select-atoms'],
    preconditions: [
        pre('selection_active', 'An atom selection must be active'),
    ],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'ExportSelectionToFormat',
    serverCommand: 'export-selection',
    jobType: null,
};

// ─── export-query ───────────────────────────────────────────────────────────

export const exportQuery = {
    id: 'export-query',
    name: 'Export by Query',
    category: 'Export & Reporting',
    description: 'Select atoms by query string, export to format, then deselect. Convenience wrapper combining selection + export.',
    inputs: [
        param('query', T.QueryString, 'Atom selection query'),
        param('format', 'string', 'Target format', true, { options: EXPORT_FORMATS }),
        param('caseData', T.CaseData, 'Protein case context'),
    ],
    outputs: [
        param('data', 'string', 'Exported data'),
    ],
    parameters: [
        param('format', 'string', 'Export format', true, { options: EXPORT_FORMATS }),
    ],
    dependsOn: [],
    preconditions: [
        pre('workspace_has_data', 'Protein or compounds must be loaded'),
    ],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'ExportQueryToFormat',
    serverCommand: null,
    jobType: null,
};

// ─── get-protein-pdb ────────────────────────────────────────────────────────

export const getProteinPdb = {
    id: 'get-protein-pdb',
    name: 'Export Protein Binding Site PDB',
    category: 'Export & Reporting',
    description: 'Generate PDB-format structure of the protein binding site around a compound. Two methods: local (fast, BMaps internal) or server (full export via selection query).',
    inputs: [
        param('input', T.Compound, 'Ligand or protein context'),
    ],
    outputs: [
        param('pdbData', T.PdbData, 'PDB structure and formatted string'),
    ],
    parameters: [
        param('method', 'string', 'Export method', false, { defaultValue: 'local', options: ['local', 'server'] }),
        param('bindingSiteDistance', 'number', 'Radius around compound for binding site', false),
    ],
    dependsOn: [],
    preconditions: [
        pre('protein_loaded', 'A protein must be loaded'),
        pre('compound_loaded', 'A compound must be loaded'),
    ],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'GetProteinPdb',
    serverCommand: null,
    jobType: null,
};

// ─── get-mol-pic ────────────────────────────────────────────────────────────

export const getMolPic = {
    id: 'get-mol-pic',
    name: 'Generate 2D Molecule Image',
    category: 'Export & Reporting',
    description: 'Render 2D structure depiction (SVG/PNG) for a SMILES string, fragment, or molecule. Used for compound thumbnails, shopping cart images, and export.',
    inputs: [
        param('smiles', T.SMILES, 'Molecule SMILES string', false),
        param('fragment', 'string', 'Fragment identifier', false),
    ],
    outputs: [
        param('image', 'string', '2D structure depiction (SVG or PNG data)'),
    ],
    parameters: [
        param('size', 'number', 'Image size in pixels', false),
        param('format', 'string', 'Output format', false, { options: ['svg', 'png'] }),
        param('highlighting', 'object', 'Atom/bond highlighting options', false),
    ],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: null,
    serverCommand: 'getMolPic',
    jobType: null,
};

// ─── Default Export ─────────────────────────────────────────────────────────

export default [
    exportCompounds,
    exportSelection,
    exportQuery,
    getProteinPdb,
    getMolPic,
];
